import { Link } from "react-router-dom";
import {
  Leaf, Sprout, HeartPulse, Award, ArrowRight, BadgeCheck, ShieldCheck,
  Sparkles, Quote, MessageCircle, Phone, Boxes, Droplets, Users, Check,
} from "lucide-react";
import {
  IMAGES, WHY_CHOOSE, PRODUCT_CATEGORIES, TESTIMONIALS, AUDIENCES, CONTACT, SEO_PAGES,
} from "@/data/site";
import {
  Eyebrow, SectionHeading, PrimaryButton, SecondaryButton, ConversionRow, TextLink,
} from "@/components/Shared";
import { Seo } from "@/components/Seo";
import { MeetFounder } from "@/components/MeetFounder";
import { CertifiedTrusted } from "@/components/CertifiedTrusted";
import { TrustBadges, useCertificate } from "@/components/CertificateProvider";

const ICONS = { leaf: Leaf, sprout: Sprout, "heart-pulse": HeartPulse, award: Award, "badge-check": BadgeCheck, "shield-check": ShieldCheck, sparkles: Sparkles };

const VALUE_CARDS = [
  { icon: Boxes, label: "Small-Batch Freshness" },
  { icon: Sprout, label: "Locally Sourced Ingredients" },
  { icon: Leaf, label: "No Artificial Additives" },
  { icon: Droplets, label: "Naturally Sweetened Options" },
  { icon: BadgeCheck, label: "FDA Registered Products", certIndex: 0 },
  { icon: Users, label: "Supporting Local Farmers" },
];

const Home = () => {
  const featured = PRODUCT_CATEGORIES.slice(0, 4);
  const { open } = useCertificate();

  return (
    <div data-testid="home-page">
      <Seo title={SEO_PAGES.home.title} description={SEO_PAGES.home.description} />
      {/* HERO */}
      <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-mist/50 blur-3xl" />
        <div className="absolute top-40 -left-32 h-80 w-80 rounded-full bg-brand-lilac/50 blur-3xl" />
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
          <div className="lg:col-span-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-mist/60 px-4 py-1.5 text-xs font-medium tracking-wide text-brand-greenDark">
              <Leaf size={14} /> Healthy Habits Start Now
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.02] text-brand-ink">
              Authentic Ghanaian Flavours. <span className="text-brand-green">Naturally Crafted.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-brand-slate">
              Experience handcrafted traditional drinks, fresh juices, wholesome
              snacks and professional catering made with fresh, locally sourced
              ingredients. Every product is prepared with care, quality and
              authenticity.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton to="/products" testid="hero-shop">
                Explore Faf's Collection <ArrowRight size={18} />
              </PrimaryButton>
              <SecondaryButton to="/catering" testid="hero-catering">
                Book an Event
              </SecondaryButton>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-trust-row">
              {["FDA Registered Products", "Certified & Trusted", "Authentic Ghanaian Recipes", "Locally Sourced Ingredients"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink">
                  <Check size={15} className="text-brand-green shrink-0" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-fade-in">
            <div className="img-zoom-wrap relative overflow-hidden rounded-[2.5rem] aspect-[4/5] max-h-[80vh]">
              <img src={IMAGES.hero} alt="Fresh healthy juice in sunlight" className="img-zoom h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-3xl bg-white border border-black/5 shadow-xl p-5 w-52">
              <p className="text-3xl font-bold text-brand-green">0g</p>
              <p className="text-sm text-brand-slate mt-1">Added sugar. Just real fruit.</p>
            </div>
          </div>
        </div>
        <div className="container-px mx-auto max-w-[1400px] mt-12 lg:mt-16 relative">
          <p className="eyebrow text-brand-slate/70 mb-3">Certified & Trusted</p>
          <TrustBadges variant="light" />
        </div>
      </section>

      {/* BRAND VALUES STRIP */}
      <section data-testid="brand-values" className="py-16 lg:py-20 border-y border-black/5">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading align="center" eyebrow="Our Promise" title="Why Families Trust Faf's" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {VALUE_CARDS.map((v) => {
              const Icon = v.icon;
              const isLink = v.certIndex != null;
              const Tag = isLink ? "button" : "div";
              return (
                <Tag
                  key={v.label}
                  type={isLink ? "button" : undefined}
                  onClick={isLink ? () => open(v.certIndex) : undefined}
                  data-testid={isLink ? "brand-value-fda" : undefined}
                  className={`group text-center rounded-3xl border border-black/5 bg-brand-alt/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-md ${isLink ? "cursor-pointer" : ""}`}
                >
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand-greenDark transition-colors group-hover:bg-brand-green group-hover:text-white">
                    <Icon size={22} />
                  </div>
                  <p className="mt-4 flex items-center justify-center gap-1.5 text-sm font-medium text-brand-ink leading-snug">
                    <Check size={14} className="text-brand-green shrink-0" />
                    {v.label}
                  </p>
                  {isLink && <span className="mt-1 block text-[11px] text-brand-green/70 group-hover:text-brand-green">View certificate</span>}
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section data-testid="why-choose" className="section-py bg-brand-alt">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading eyebrow="Why Choose Faf's" title="Crafted with intention, made for wellness" intro="We believe healthy should never mean compromise. Every product is a promise of purity, flavour and care." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_CHOOSE.map((f, i) => {
              const Icon = ICONS[f.icon];
              return (
                <div
                  key={f.title}
                  data-testid={`why-card-${i}`}
                  className="group rounded-3xl bg-white border border-black/5 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-mist text-brand-greenDark transition-colors group-hover:bg-brand-green group-hover:text-white">
                    <Icon size={26} />
                  </div>
                  <h3 className="mt-6 text-xl font-medium text-brand-ink">{f.title}</h3>
                  <p className="mt-3 text-brand-slate leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section data-testid="featured-products" className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Featured Products" title="A taste of what we make" />
            <TextLink to="/products" testid="featured-view-all">View all products</TextLink>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
            {featured.map((p, i) => (
              <Link
                key={p.id}
                to="/products"
                data-testid={`featured-card-${p.id}`}
                className={`group relative overflow-hidden rounded-3xl border border-black/5 ${i === 0 ? "sm:col-span-2 sm:row-span-1" : ""}`}
              >
                <div className="img-zoom-wrap relative overflow-hidden aspect-[4/3] sm:h-full">
                  <img src={p.image} alt={p.name} className="img-zoom h-full w-full object-cover min-h-[260px]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <p className="text-xs tracking-widest uppercase text-white/70">{p.tagline}</p>
                    <h3 className="mt-1 text-2xl font-semibold text-white">{p.name}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CATERING */}
      <section data-testid="home-catering" className="section-py bg-brand-greenDark text-white overflow-hidden">
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="img-zoom-wrap relative overflow-hidden rounded-[2rem] aspect-[5/4] order-2 lg:order-1">
            <img src={IMAGES.catering} alt="Faf's event drink and snack bar set up at a celebration in Ghana" className="img-zoom h-full w-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow light>Catering & Events</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              A premium healthy drink bar for your big moment
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed text-base md:text-lg">
              From corporate functions and weddings to church events and school
              fairs — we bring a beautifully styled, fully serviced Faf's drink
              bar to your venue. Custom menus, branded glassware, unforgettable
              experience.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/catering" data-testid="home-catering-cta" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-medium text-brand-greenDark transition-all duration-300 hover:-translate-y-0.5">
                Explore Catering <ArrowRight size={18} />
              </Link>
              <a href={CONTACT.whatsappCateringHref} target="_blank" rel="noopener noreferrer" data-testid="home-catering-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-white/10">
                <MessageCircle size={18} /> Enquire
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HEALTHY LIVING */}
      <section data-testid="home-healthy-living" className="section-py">
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Eyebrow>Healthy Living</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight text-brand-ink">
              Wellness is a lifestyle, not a moment
            </h2>
            <p className="mt-5 text-brand-slate leading-relaxed text-base md:text-lg">
              Faf's is more than what's in the bottle. We share tips, recipes
              and inspiration to help you and your family live well, naturally —
              one healthy choice at a time.
            </p>
            <ul className="mt-7 space-y-3">
              {["Nutrition guidance you can trust", "Simple recipes for busy families", "Honest, label-clear ingredients"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-brand-ink">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-brand-mist text-brand-greenDark"><Leaf size={13} /></span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <PrimaryButton to="/products" testid="home-healthy-cta">
                Explore Our Products <ArrowRight size={18} />
              </PrimaryButton>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="img-zoom-wrap overflow-hidden rounded-[2rem] aspect-[3/4] mt-8">
              <img src={IMAGES.healthyLiving} alt="Healthy lifestyle" className="img-zoom h-full w-full object-cover" />
            </div>
            <div className="img-zoom-wrap overflow-hidden rounded-[2rem] aspect-[3/4]">
              <img src={IMAGES.veggies} alt="Fresh vegetables" className="img-zoom h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* MEET FOUNDER */}
      <MeetFounder />

      {/* CERTIFIED & TRUSTED */}
      <CertifiedTrusted />

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading eyebrow="Testimonials" title="Trusted by families & brands across Ghana" />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="rounded-3xl bg-brand-alt border border-black/5 p-8 flex flex-col">
                <Quote size={30} className="text-brand-purple/40" />
                <blockquote className="mt-4 text-brand-ink leading-relaxed font-light flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-black/5">
                  <p className="font-medium text-brand-ink">{t.name}</p>
                  <p className="text-sm text-brand-slate">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            <span className="text-sm text-brand-slate mr-2">Proudly serving:</span>
            {AUDIENCES.map((a) => (
              <span key={a} className="rounded-full border border-black/10 px-4 py-1.5 text-sm text-brand-ink">{a}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section data-testid="home-cta" className="pb-20 lg:pb-32">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-green px-8 py-16 lg:px-20 lg:py-24 text-center">
            <div className="absolute -top-16 -right-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-brand-purple/30 blur-3xl" />
            <div className="relative">
              <h2 className="mx-auto max-w-3xl text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight text-white">
                Ready to taste the freshest in Ghana?
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-white/80 text-base md:text-lg">
                Order your favourites or book a Faf's drink bar for your next
                event. We're just a message away.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <a href={CONTACT.whatsappOrderHref} target="_blank" rel="noopener noreferrer" data-testid="cta-final-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-medium text-brand-greenDark transition-all duration-300 hover:-translate-y-0.5">
                  <MessageCircle size={18} /> Order on WhatsApp
                </a>
                <a href={CONTACT.phoneHref} data-testid="cta-final-call" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-white/10">
                  <Phone size={18} /> {CONTACT.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
